// Copy for the landing page. Keep claims factual — no invented metrics.

export const practices = [
  {
    numeral: 'I',
    id: 'bespoke',
    name: 'Bespoke software',
    headline: 'When off-the-shelf stops fitting.',
    summary: 'New platforms, portals, and internal tools.',
    body: 'Custom web applications, customer portals, commerce, and internal tools, designed around how your business actually runs. Built so the next engineer can maintain it without calling us.',
    items: [
      'Web and mobile applications',
      'Commerce and customer portals',
      'Internal tools and integrations',
      'Fractional technical leadership',
    ],
    link: { to: '/services/web-development', label: 'Bespoke software' },
  },
  {
    numeral: 'II',
    id: 'modernization',
    name: 'Modernization',
    headline: 'Legacy code, brought forward.',
    summary: 'Aging codebases, moved forward safely.',
    body: 'Your system works. It is just getting harder to change. We upgrade frameworks, add TypeScript and tests, and untangle the parts everyone is afraid to touch. No risky rewrite, and no frozen roadmap.',
    items: [
      'Framework and dependency upgrades',
      'JavaScript to TypeScript migrations',
      'Test coverage and CI/CD',
      'Incremental rewrites with no downtime',
    ],
    link: { to: '/services/consulting', label: 'Modernization' },
  },
  {
    numeral: 'III',
    id: 'refinement',
    name: 'Refinement',
    headline: 'The details that cost you customers.',
    summary: 'Bugs, speed, copy, and search.',
    body: 'Some engagements take a week, not a quarter. We find and fix the bug, speed up the slow page, sharpen the copy, and make sure the right people can find you in search.',
    items: [
      'Bug triage and fixes',
      'Core Web Vitals and accessibility',
      'Conversion copywriting',
      'Technical and local SEO',
    ],
    link: { to: '/services/seo', label: 'Refinement' },
  },
];

// Case study ids from caseStudies.js, in display order, with cropped covers
export const featuredWork = [
  { id: 'topazery-commerce', cover: 'covers/topazery.webp' },
  { id: 'jku-leadgen', cover: 'covers/jku.webp' },
  { id: 'sbs-launch', cover: 'covers/sbs.webp' },
];

export const clientNames = [
  'C Spire',
  'DataMoon',
  'Topazery',
  'Lovebook',
  'StationeryHQ',
  'Serendip',
  'SBS Property Transformations',
  'JK Unlimited Services',
  'GDG Detroit',
  'Compass Detroit',
];

export const method = [
  {
    step: '01',
    name: 'Diagnose',
    body: 'We listen first, then read the code. You get a written assessment of what is there, what is at risk, and what matters most.',
  },
  {
    step: '02',
    name: 'Propose',
    body: 'A defined scope, timeline, and estimate, written in plain English and agreed before any work begins.',
  },
  {
    step: '03',
    name: 'Build',
    body: 'Short cycles with working demos. You see real progress, not status reports.',
  },
  {
    step: '04',
    name: 'Hand over',
    body: 'Documentation, tests, and a walkthrough, so your team owns what we built. Ongoing care is available if you want it.',
  },
];

export const serviceArea = [
  'Detroit',
  'Royal Oak',
  'Troy',
  'Birmingham',
  'Southfield',
  'Novi',
  'Ann Arbor',
  'Dearborn',
  'Sterling Heights',
  'Farmington Hills',
];

export const contactEmail = 'info@shumunovsolutions.com';

// Toolkit band — keep to tools actually used on client work
export const toolkit = [
  'TypeScript',
  'React',
  'Next.js',
  'Node.js',
  'Laravel',
  'PHP',
  'Python',
  'PostgreSQL',
  'MongoDB',
  'AWS',
  'Docker',
  'React Native',
];

// Monochrome marks generated from public/img/clients into public/img/clients/mono
export const clientLogos = [
  { label: 'C Spire', src: '/img/clients/mono/cspire.png' },
  { label: 'DataMoon', src: '/img/clients/mono/datamoon.png' },
  { label: 'Topazery', src: '/img/clients/mono/topazery.png' },
  { label: 'LoveBook', src: '/img/clients/mono/lovebook.png' },
  { label: 'StationeryHQ', src: '/img/clients/mono/stationeryhq.png' },
  { label: 'Serendip', src: '/img/clients/mono/serendip.png' },
  { label: 'SBS Property Transformations', src: '/img/clients/mono/sbs.png' },
  { label: 'JK Unlimited Services', src: '/img/clients/mono/jk-unlimited.png' },
  { label: 'Frecklebox', src: '/img/clients/mono/frecklebox.png' },
  { label: 'Paper Goods Warehouse', src: '/img/clients/mono/paper-goods.png' },
  { label: 'Neuron Publishing', src: '/img/clients/mono/neuron.png' },
  { label: 'Your Wired Solutions', src: '/img/clients/mono/yws.png' },
  { label: 'GDG Detroit', src: '/img/clients/mono/gdg-detroit.png' },
  { label: 'Compass Detroit', src: '/img/clients/mono/compass-detroit.png' },
];

// Volunteer engineering for Detroit's tech community. Figures from the GitHub orgs (Sept 2026).
export const community = {
  stats: [
    { value: '9', label: 'Community repositories' },
    { value: '950+', label: 'Commits contributed' },
    { value: '7', label: 'Event sites shipped' },
  ],
  offerings: ['Technical workshops', 'Conference talks', 'Event web development'],
  // Sessions given at community events (DevFest 2025 per GDG-Detroit/devfest-website speakers data)
  talks: [
    {
      format: 'Workshop',
      title: 'Component Catalyst: Building a Dynamic React Portfolio',
      event: 'Michigan DevFest 2025',
      href: 'https://devfest-website.vercel.app',
      summary:
        'A hands-on React workshop on component architecture and data-driven UI, ending with a portfolio deployed live for résumés and social profiles.',
    },
    {
      format: 'Workshop',
      title: 'Coding at the Speed of Thought: Professional Portfolio',
      event: 'Detroit Pride Innovation Summit',
      href: 'https://pridemi26.vercel.app',
      summary:
        'AI agents as engineering partners, not autocomplete: prompting with intent, orchestrating agents for rapid prototyping, and keeping the architecture sound.',
    },
    {
      format: 'Talk',
      title: 'Pull the Cord. Bring Backup.',
      event: 'LHM Innovation Summit',
      summary:
        'Stop typing boilerplate and point an AI crew at your own bottlenecks. Agentic development, as taught by Jack Reacher and Chainsaw Man.',
    },
  ],
  orgs: [
    {
      name: 'GDG Detroit',
      role: 'Google Developer Group',
      logo: '/img/orgs/gdg-detroit.svg',
      summary:
        'Lead contributor to the Michigan DevFest website, and a hands-on React workshop at DevFest 2025.',
      github: 'https://github.com/GDG-Detroit',
      projects: [
        { name: 'Michigan DevFest', href: 'https://devfest-website.vercel.app' },
        { name: 'Michigan DevFest 2026', href: 'https://midevfest26.vercel.app' },
      ],
    },
    {
      name: 'Compass Detroit',
      role: 'Innovation summits & community',
      logo: '/img/orgs/compass-detroit.svg',
      summary:
        'Core contributor to the Compass Detroit site and the event sites behind its innovation summits, plus workshops and talks at the summits themselves.',
      github: 'https://github.com/Compass-Detroit',
      projects: [
        { name: 'Compass Detroit', href: 'https://compass-detroit.com' },
        { name: 'Black History Month Innovation Summit', href: 'https://bhmsummit.com' },
        { name: "International Women's Day Summit", href: 'https://iwdsummit.vercel.app' },
        { name: 'Pride MI 2026', href: 'https://pridemi26.vercel.app' },
        { name: 'Michigan Hackathon 2026', href: 'https://www.hackmichigan.com' },
        { name: 'Michigan SHPE Innovation Summit', href: 'https://mishpe26.vercel.app' },
      ],
    },
  ],
};

// Plain answers to the questions buyers ask first. Also emitted as FAQPage JSON-LD,
// so every answer must stay true and stand on its own when quoted.
export const faq = [
  {
    q: 'What does Shumunov Solutions do?',
    a: 'Shumunov Solutions is a founder-led software studio in Metro Detroit. We build bespoke web and mobile software, modernize legacy codebases without a risky rewrite, and refine existing products: bug fixes, Core Web Vitals, accessibility, conversion copy, and technical and local SEO.',
  },
  {
    q: 'Who will I actually work with?',
    a: 'You work directly with the engineer doing the work. There are no account managers and no hand-offs between a sales team and a delivery team, so the person who scopes the project is the person who builds it.',
  },
  {
    q: 'Do you only work with Detroit companies?',
    a: 'No. We meet in person across Metro Detroit, including Oakland, Macomb, and Wayne counties, and work remotely with teams anywhere in the United States who want a partner in an Eastern time zone.',
  },
  {
    q: 'Will you rewrite our legacy application from scratch?',
    a: 'Rarely. Most aging systems are better served by upgrading frameworks and dependencies, migrating JavaScript to TypeScript, adding tests and CI/CD, and rewriting the riskiest parts incrementally with no downtime. Your roadmap keeps moving while the codebase improves.',
  },
  {
    q: 'How do projects start, and how is pricing set?',
    a: 'Every engagement starts with a diagnosis: we listen, read the code, and give you a written assessment of what is there, what is at risk, and what matters most. You then get a defined scope, timeline, and estimate in plain English, agreed before any work begins.',
  },
  {
    q: 'Can you take on a small fix instead of a full project?',
    a: 'Yes. Some engagements take a week, not a quarter: fixing a stubborn bug, speeding up a slow page, sharpening copy that is not converting, or making sure the right people can find you in search.',
  },
  {
    q: 'What technologies do you work with?',
    a: 'Day to day: TypeScript, React, Next.js, Node.js, Laravel and PHP, Python, PostgreSQL, MongoDB, AWS, Docker, and React Native. We choose tools your team can maintain after we leave.',
  },
  {
    q: 'What happens after launch?',
    a: 'We hand over documentation, tests, and a walkthrough so your team owns what we built. Ongoing care is available if you want it, but you are never locked in.',
  },
];
